import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationError } from "../../shared/validation.error";
import { UseCase } from "../../shared/use-case";
import { TranslateUseCaseRequest } from "./translate.use-case-request";
import { TranslateUseCaseResponse } from "./translate.use-case-response";
import { LocalStorageTranslations, TranslationStore } from "../translation";
import { BusinessLogicError } from "../../shared/business-logic.error";
import { LogService } from "../../shared/log.service";
import { TranslationConfig } from "../translation-config";

@Injectable({
    providedIn: 'root'
})
export class TranslateUseCase extends UseCase<TranslateUseCaseRequest, TranslateUseCaseResponse> {

    constructor(
        _logService: LogService,
        _httpClient: HttpClient,
        _translationConfig: TranslationConfig
    ) {
        super(_logService, _httpClient, _translationConfig.enableUseCaseStatusLogs);
    }

    protected override validate(request: TranslateUseCaseRequest) {
        if (!request.translationKey){
            new ValidationError("Empty translation key cannot be translated!");
        }
        if (!request.language){
            new ValidationError("Translations has no available language!");
        }
        if (!request.nameOfLocalStorage){
            new ValidationError("Translations has no available local storage!");
        }
    }

    protected override executeBusinessLogic(request: TranslateUseCaseRequest): TranslateUseCaseResponse {
        let response: TranslateUseCaseResponse = { translatedText: request.translationKey };
        const splittedKey = request.translationKey.split('.', request.translationKey.length);
        let keyIndex = 0;
        let translation: TranslationStore | string = this._getTranslationsForLanguage(request.language, request.nameOfLocalStorage);

        if (!this._isTranslationStoreContainsKeyPart(translation, splittedKey[keyIndex])) {
            return response;
        }

        while (this._isTranslationStoreContainsKeyPart(translation, splittedKey[keyIndex])) {
            translation = (translation as TranslationStore)[splittedKey[keyIndex]];
            keyIndex++;
        }

        if (!translation) {
            return response;
        }
        
        return { translatedText: translation as string };
    }

    private _getTranslationsForLanguage(language: string, nameOfLocalStorage: string): TranslationStore{
        const translations = localStorage.getItem(nameOfLocalStorage) as string;
        if (translations) {
            const localStorageTranslations: LocalStorageTranslations[] = JSON.parse(translations) as LocalStorageTranslations[];
            let translationsForLanguage: TranslationStore | undefined;
            localStorageTranslations.forEach((translation) => {
                if (translation.language == language) {
                    translationsForLanguage = translation.translations as TranslationStore;
                }
            });
            if (translationsForLanguage) {
                return translationsForLanguage;
            }
            throw new BusinessLogicError(`Cannot find translation for language: ${language}`);
        }
        throw new BusinessLogicError(`Cannot find translations in store: ${nameOfLocalStorage}`);
    }

    private _isTranslationStoreContainsKeyPart(translation: TranslationStore | string | undefined, keyPart: string): boolean {
        return translation !== undefined && JSON.stringify(translation as Object).includes(keyPart);
    }
    
}